import React, { useMemo, useState } from 'react';
import PropTypes from "prop-types"

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { DateRangePicker } from "react-date-range";
import ReactSelect from 'react-select';

import {
  addDays,
  format,
  isEqual,
  subMilliseconds,
  subDays,
  subWeeks,
  subMonths,
  subQuarters,
  subYears,
  addHours,
  subHours
} from 'date-fns';

import {
  BROWSER_IANA_TIMEZONE,
  getZonedEndOfDay,
  getZonedStartOfDay,
  getZonedStartOfMonth,
  getZonedStartOfQuarter,
  getZonedStartOfWeek,
  getZonedStartOfYear
} from 'helpers/time_utils';

import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import useDidMountEffect from 'hooks/useDidMount';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { getPrimaryButtonClass } from 'helpers/sf_utils';
import { REPORTING_TIMEZONE } from 'helpers/sf_constants';

export const DATE_RANGE_OPTIONS = {
  ALL_TIME: 'ALL_TIME',
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  THIS_WEEK: 'THIS_WEEK',
  LAST_WEEK: 'LAST_WEEK',
  LAST_7_DAYS: 'LAST_7_DAYS',
  THIS_MONTH: 'THIS_MONTH',
  LAST_MONTH: 'LAST_MONTH',
  LAST_30_DAYS: 'LAST_30_DAYS',
  LAST_60_DAYS: 'LAST_60_DAYS',
  THIS_QUARTER: 'THIS_QUARTER',
  LAST_QUARTER: 'LAST_QUARTER',
  THIS_YEAR: 'THIS_YEAR',
  LAST_YEAR: 'LAST_YEAR',
  CUSTOM_RANGE: 'CUSTOM_RANGE'
}

export const COMPARE_DATE_RANGE_OPTIONS = {
  ...DATE_RANGE_OPTIONS,
  SAME_DAY_LAST_WEEK: 'SAME_DAY_LAST_WEEK'
}

export const TIME_RANGE_OPTIONS = {
  LAST_HOUR: 'LAST_HOUR',
  LAST_6_HOURS: 'LAST_6_HOURS',
  ...DATE_RANGE_OPTIONS
}

export const RDS_DATE_RANGE_OPTIONS_WITH_TIME = {
  ALL_TIME: 'ALL_TIME',
  LAST_HOUR: 'LAST_HOUR',
  LAST_6_HOURS: 'LAST_6_HOURS',
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  THIS_WEEK: 'THIS_WEEK',
  LAST_WEEK: 'LAST_WEEK',
  LAST_7_DAYS: 'LAST_7_DAYS',
  CUSTOM_RANGE: 'CUSTOM_RANGE'
}

export const DATE_RANGE_OPTIONS_DISPLAY = {
  ALL_TIME: 'All Time',
  LAST_HOUR: 'Last Hour',
  LAST_6_HOURS: 'Last 6 Hours',
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  SAME_DAY_LAST_WEEK: 'Same Day Last Week',
  THIS_WEEK: 'This Week',
  LAST_WEEK: 'Last Week',
  LAST_7_DAYS: 'Last 7 Days',
  THIS_MONTH: 'This Month',
  LAST_MONTH: 'Last Month',
  LAST_30_DAYS: 'Last 30 Days',
  LAST_60_DAYS: 'Last 60 Days',
  THIS_QUARTER: 'This Quarter',
  LAST_QUARTER: 'Last Quarter',
  THIS_YEAR: 'This Year',
  LAST_YEAR: 'Last Year',
  CUSTOM_RANGE: 'Custom Range'
}

export const SINGLE_DATE_OPTIONS = {
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  CUSTOM_RANGE: 'CUSTOM_RANGE'
}

const CUSTOM_RANGE_LABEL = 'Custom Range'
const CALENDAR_SELECTION_KEY = 'calendar_selection'

/**
 * 
 * @param {Date} dt start of day
 * @param {String} ianaTz
 * @returns start of day after considering DST
 */
export const getStartOfDayHandleDST = (dt, ianaTz) => {
  return getZonedStartOfDay(addHours(dt, 2), ianaTz);
}

const getStartAndEndDateForLabel = (label, today, ianaTz) => {
  let startDate = null;
  let endDate = null;
  switch (label) {
    case TIME_RANGE_OPTIONS.LAST_HOUR:
      startDate = subHours(today, 1)
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case TIME_RANGE_OPTIONS.LAST_6_HOURS:
      startDate = subHours(today, 6)
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.TODAY:
      startDate = getZonedStartOfDay(today, ianaTz);
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.YESTERDAY:
      const yesterdayDate = subDays(today, 1);
      startDate = getZonedStartOfDay(yesterdayDate, ianaTz);
      endDate = getZonedEndOfDay(yesterdayDate, ianaTz);
      break;
    case COMPARE_DATE_RANGE_OPTIONS.SAME_DAY_LAST_WEEK: {
      // Subtract calendar days in the reporting timezone, including across DST.
      const sameDayLastWeek = zonedTimeToUtc(subDays(utcToZonedTime(today, ianaTz), 7), ianaTz);
      startDate = getZonedStartOfDay(sameDayLastWeek, ianaTz);
      endDate = getZonedEndOfDay(sameDayLastWeek, ianaTz);
      break;
    }
    case DATE_RANGE_OPTIONS.THIS_WEEK:
      startDate = getZonedStartOfWeek(today, ianaTz);
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.LAST_WEEK:
      const startOfThisWeek = getZonedStartOfWeek(today, ianaTz);
      startDate = getStartOfDayHandleDST(subWeeks(startOfThisWeek, 1), ianaTz);
      endDate = subMilliseconds(startOfThisWeek, 1);
      break;
    case DATE_RANGE_OPTIONS.LAST_7_DAYS:
      // XXX: we subtract only 6 days since the current day is also included
      startDate = getStartOfDayHandleDST(subDays(getZonedStartOfDay(today, ianaTz), 6), ianaTz);
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.THIS_MONTH:
      startDate = getZonedStartOfMonth(today, ianaTz);
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.LAST_MONTH:
      const startOfThisMonth = getZonedStartOfMonth(today, ianaTz);
      startDate = getStartOfDayHandleDST(subMonths(startOfThisMonth, 1), ianaTz);
      endDate = subMilliseconds(startOfThisMonth, 1);
      break;
    case DATE_RANGE_OPTIONS.LAST_30_DAYS:
      // we subtract only 29 days since the current day is also included
      startDate = getStartOfDayHandleDST(subDays(getZonedStartOfDay(today, ianaTz), 29), ianaTz);
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.LAST_60_DAYS:
      // we subtract only 59 days since the current day is also included
      startDate = getStartOfDayHandleDST(subDays(getZonedStartOfDay(today, ianaTz), 59), ianaTz);
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.THIS_QUARTER:
      startDate = getZonedStartOfQuarter(today, ianaTz);
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.LAST_QUARTER:
      const startOfThisQuarter = getZonedStartOfQuarter(today, ianaTz);
      startDate = getStartOfDayHandleDST(subQuarters(startOfThisQuarter, 1), ianaTz);
      endDate = subMilliseconds(startOfThisQuarter, 1);
      break;
    case DATE_RANGE_OPTIONS.THIS_YEAR:
      startDate = getZonedStartOfYear(today, ianaTz);
      endDate = getZonedEndOfDay(today, ianaTz);
      break;
    case DATE_RANGE_OPTIONS.LAST_YEAR:
      const startOfThisYear = getZonedStartOfYear(today, ianaTz);
      startDate = getStartOfDayHandleDST(subYears(startOfThisYear, 1), ianaTz);
      endDate = subMilliseconds(startOfThisYear, 1);
      break;
    case DATE_RANGE_OPTIONS.ALL_TIME:
      startDate = null
      endDate = null
      break;
    case DATE_RANGE_OPTIONS.CUSTOM_RANGE:
      startDate = CUSTOM_RANGE_LABEL;
      endDate = CUSTOM_RANGE_LABEL;
      break;
    default:
      startDate = today;
      endDate = today;
      break;
  }

  return { startDate, endDate };
}

export const createOption = (label, today = new Date(), ianaTz = BROWSER_IANA_TIMEZONE) => {
  if (ianaTz === undefined || ianaTz === null) {
    ianaTz = BROWSER_IANA_TIMEZONE;
  }
  return {
    label: DATE_RANGE_OPTIONS_DISPLAY[label],
    value: getStartAndEndDateForLabel(label, today, ianaTz),
    original_label: label,
    ianaTz: ianaTz
  }
}

export const createOptionWithDates = (label, start_date, end_date) => {
  return {
    label: label,
    value: { start_date, end_date },
    original_label: label,
    ianaTz: BROWSER_IANA_TIMEZONE
  }
}

export const createLabelWithDateRange = ({ label, startDate, endDate }) => {
  const ianaTz = REPORTING_TIMEZONE;
  if (label === DATE_RANGE_OPTIONS_DISPLAY.ALL_TIME || !startDate || !endDate) {
    return label;
  }

  if (isEqual(getZonedStartOfDay(startDate, ianaTz), getZonedStartOfDay(endDate, ianaTz))) {
    return label.length > 0 ? `${label}: ${format(utcToZonedTime(startDate, ianaTz), 'MMM d, yyyy')}` :
      `${format(utcToZonedTime(startDate, ianaTz), 'MMM d, yyyy')}`
  }
  if (label.length > 0) {
    return `${label}: ${format(utcToZonedTime(startDate, ianaTz), 'MMM d')} - ${format(utcToZonedTime(endDate, ianaTz), 'MMM d, yyyy')}`
  }
  else {
    return `${format(utcToZonedTime(startDate, ianaTz), 'MMM d')} - ${format(utcToZonedTime(endDate, ianaTz), 'MMM d, yyyy')}`

  }
}

const CustomDateRangePicker = (props) => {
  // maintain two states of selected date, one from drop down and another from actual react-date-range
  // call props.onChange when a date range is selected
  const [selectedOption, setSelectedOption] = useState(props.value)
  const [calendarRangeState, setCalendarRangeState] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: CALENDAR_SELECTION_KEY
  })
  const [showCalendar, setShowCalendar] = useState(false)
  const tz = props.ianaTz || BROWSER_IANA_TIMEZONE;

  const handleSelectedOption = (opt) => {
    setSelectedOption({ ...opt });
  }

  useDidMountEffect(() => {
    if (selectedOption.value.startDate !== CUSTOM_RANGE_LABEL) {
      props.onChange({
        label: createLabelWithDateRange(
          {
            label: selectedOption.label,
            startDate: selectedOption.value.startDate,
            endDate: selectedOption.value.endDate
          }
        ),
        value: selectedOption.value,
        original_label: selectedOption.original_label,
        ianaTz: tz
      })
      return;
    }

    setShowCalendar(true)
  }, [selectedOption])

  const handleCalendarApply = () => {
    const startDateFromCalendarWithTz = zonedTimeToUtc(new Date(calendarRangeState.startDate.toDateString()), tz);
    const endDateFromCalendarWith = subMilliseconds(addDays(zonedTimeToUtc(new Date(calendarRangeState.endDate.toDateString()), tz), 1), 1);

    const newOption = {
      label: createLabelWithDateRange({ label: CUSTOM_RANGE_LABEL, startDate: startDateFromCalendarWithTz, endDate: endDateFromCalendarWith }),
      value: { startDate: startDateFromCalendarWithTz, endDate: endDateFromCalendarWith },
      original_label: DATE_RANGE_OPTIONS.CUSTOM_RANGE,
      ianaTz: tz
    }

    props.onChange(newOption)
    setShowCalendar(false)
  }

  const calendarRanges = useMemo(() => {
    return props?.allowSingleDateSelection ? [{
      ...calendarRangeState,
      endDate: calendarRangeState.startDate
    }] : [calendarRangeState];
  }, [calendarRangeState, props?.allowSingleDateSelection]);

  return (
    <>
      {showCalendar && (
        <>
          <Modal size='xl' isOpen={showCalendar} toggle={() => setShowCalendar(false)} centered={true}>
            <ModalBody className="py-3 px-5" >
              <DateRangePicker
                onChange={(val) => {
                  const selectedRange = val[CALENDAR_SELECTION_KEY];
                  if (props?.allowSingleDateSelection) {
                    // For single date mode, set end date same as start date
                    setCalendarRangeState({
                      ...selectedRange,
                      endDate: selectedRange.startDate
                    });
                  } else {
                    setCalendarRangeState(selectedRange);
                  }
                }}
                moveRangeOnFirstSelection={false}
                months={1}
                ranges={calendarRanges}
                editableDateInputs={false}
                direction="horizontal"
                className="w-100"
                {...(props?.maxDate ? { maxDate: props.maxDate } : {})}
              />
            </ModalBody>
            <ModalFooter>
              <Button onClick={handleCalendarApply} className={`btn ${getPrimaryButtonClass()}`} type='button'>Apply</Button>
            </ModalFooter>
          </Modal>
        </>
      )}

      <ReactSelect
        menuPlacement={props?.menuPlacement || "auto"}
        menuPortalTarget={props?.menuPortalTarget}
        menuPosition={props?.menuPosition || "absolute"}
        closeMenuOnScroll={props?.closeMenuOnScroll}
        menuShouldScrollIntoView={props?.menuShouldScrollIntoView}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 999999,
            position: 'fixed'
          }),
          ...props?.styles
        }}
        value={props.value}
        options={props.options}
        onChange={handleSelectedOption}
        blurInputOnSelect={true}
        maxMenuHeight={props?.maxMenuHeight || 400}
        classNamePrefix='select2-selection'
      />
    </>
  )
}

CustomDateRangePicker.propTypes = {
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  ianaTz: PropTypes.string,
  menuPlacement: PropTypes.string,
  menuPortalTarget: PropTypes.object,
  menuPosition: PropTypes.string,
  closeMenuOnScroll: PropTypes.bool,
  menuShouldScrollIntoView: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  styles: PropTypes.object,
  maxDate: PropTypes.instanceOf(Date),
}

export default CustomDateRangePicker;
